import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useResearchStore } from '@/stores/researchStore';
import { useAppStore } from '@/stores/appStore';
import {
  createScheduledSearch,
  createSavedSearch,
  listInstantlyCampaignOptions,
  listSavedSearches,
  type InstantlyCampaignOption,
} from '@/services/research';
import { getAccountSettings } from '@/services/account';

interface DataFieldsInfoModalProps {
  open: boolean;
  onClose: () => void;
}

// LinkedIn Scrape Progress Modal
export function LinkedInScrapeModal() {
  const { linkedinScrapeModalOpen, linkedinScrapeProgress, linkedinScrapeStatus } = useResearchStore();

  if (!linkedinScrapeModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-card-dark border border-border-dark rounded-xl max-w-md w-full mx-4 p-6 text-center">
        <div className="mb-4">
          <img
            src="https://cdn-icons-png.flaticon.com/512/174/174857.png"
            className="w-16 h-16 mx-auto animate-pulse"
            alt="LinkedIn"
          />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Scraping LinkedIn Profile</h3>
        <p className="text-gray-400 mb-4">This may take up to 60 seconds...</p>
        <div className="w-full bg-gray-700 rounded-full h-2 mb-4">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
            style={{ width: `${linkedinScrapeProgress}%` }}
          />
        </div>
        <p className="text-sm text-gray-500">{linkedinScrapeStatus}</p>
      </div>
    </div>
  );
}

// LinkedIn Results Modal
export function LinkedInResultsModal() {
  const { linkedinResultsModalOpen, currentLinkedinData, closeLinkedinResultsModal } = useResearchStore();

  if (!linkedinResultsModalOpen || !currentLinkedinData) return null;

  const raw = currentLinkedinData.raw || [];
  const firstPost = (raw as Array<{ author?: { name?: string; info?: string } }>).find((r) => r.author) || {};
  const author = firstPost.author || {};
  const companyData = (currentLinkedinData.companyData?.[0] || {}) as {
    name?: string;
    industry?: string;
    employeeCount?: string;
    staffCount?: string;
    description?: string;
  };

  const posts = (raw as Array<{ type?: string; content?: string; postedAt?: { postedAgoText?: string }; engagement?: { likes?: number } }>).filter(
    (r) => r.type === 'post' && r.content
  );

  const exportData = () => {
    const blob = new Blob([JSON.stringify(currentLinkedinData, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `linkedin_data_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-card-dark border border-border-dark rounded-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center space-x-3">
            <img src="https://cdn-icons-png.flaticon.com/512/174/174857.png" className="w-10 h-10" alt="LinkedIn" />
            <div>
              <h3 className="text-xl font-bold text-white">{author.name || 'LinkedIn Profile'}</h3>
              <p className="text-sm text-gray-400">{author.info || ''}</p>
            </div>
          </div>
          <button onClick={closeLinkedinResultsModal} className="text-gray-400 hover:text-white text-2xl">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Profile Summary */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">Profile Summary</h4>
            <p className="text-gray-300 text-sm">{author.info || 'No summary available.'}</p>
          </div>

          {/* Recent Posts */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">Recent Activity</h4>
            {posts.length > 0 ? (
              <div className="space-y-3">
                {posts.slice(0, 3).map((post, idx) => (
                  <div key={idx} className="border-l-2 border-blue-500 pl-3">
                    <p className="text-gray-300 text-sm">
                      {(post.content || '').substring(0, 200)}
                      {(post.content || '').length > 200 ? '...' : ''}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {post.postedAt?.postedAgoText || ''} • {post.engagement?.likes || 0} likes
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No recent posts found.</p>
            )}
          </div>

          {/* Company Info */}
          {companyData && Object.keys(companyData).length > 0 && (
            <div className="bg-gray-900 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-400 mb-2">Company Intel</h4>
              <div className="text-gray-300 text-sm space-y-1">
                <p><strong>Company:</strong> {companyData.name || 'N/A'}</p>
                <p><strong>Industry:</strong> {companyData.industry || 'N/A'}</p>
                <p><strong>Size:</strong> {companyData.employeeCount || companyData.staffCount || 'N/A'}</p>
                {companyData.description && (
                  <p><strong>Description:</strong> {companyData.description.substring(0, 200)}{companyData.description.length > 200 ? '...' : ''}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Upsell Banner */}
        <div className="mt-6 bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="text-2xl">💡</div>
            <div className="flex-1">
              <h4 className="text-white font-semibold mb-1">Want more data?</h4>
              <p className="text-gray-300 text-sm mb-3">
                Export full LinkedIn profiles and contact data via CSV, or get it delivered directly to your CRM.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={exportData}
                  className="bg-white text-gray-900 px-4 py-2 rounded text-sm font-semibold hover:bg-gray-200"
                >
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DataFieldsInfoModal({ open, onClose }: DataFieldsInfoModalProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-card-dark border border-border-dark rounded-xl max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-2xl font-bold text-white">What Does “200+ Data Fields” Mean?</h3>
            <p className="text-gray-400 mt-1">
              Each contact profile includes structured information across identity, company, and verification layers — not just a name and email.
            </p>
            <div className="inline-flex mt-3 px-3 py-1 rounded-full text-xs bg-gray-800 text-gray-300 border border-gray-700">
              200+ structured attributes per record
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <div className="text-sm text-gray-400 mb-2">
              You can click Search and build lists as many times as you need without charge! Clicking "Get Data" is where you will begin using credits.
            </div>
          </div>
          <div className="bg-gradient-to-r from-red-900/40 to-transparent border border-red-900/40 rounded-lg px-4 py-3">
            <div className="text-white font-semibold text-sm">This is just the start of our lead intelligence system.</div>
          </div>

          <details className="bg-gray-900 rounded-lg p-4 border border-gray-800" open>
            <summary className="cursor-pointer text-white font-semibold">Contact Information</summary>
            <p className="text-gray-400 text-sm mt-2">Core identifying details for outreach and segmentation.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-300 mt-3">
              <div>First Name</div>
              <div>Last Name</div>
              <div>Job Title</div>
              <div>Department</div>
              <div>Seniority Level</div>
              <div>Company Name</div>
              <div>Work Email(s)</div>
              <div>Direct Dial</div>
              <div>Mobile Phone</div>
              <div>LinkedIn URL</div>
              <div>Location</div>
            </div>
            <p className="text-xs text-gray-500 mt-3">This is the foundation of every contact profile.</p>
          </details>

          <details className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <summary className="cursor-pointer text-white font-semibold">Company Intelligence</summary>
            <p className="text-gray-400 text-sm mt-2">Organizational attributes that help you qualify accounts.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-300 mt-3">
              <div>Industry</div>
              <div>Employee Count</div>
              <div>Revenue Range</div>
              <div>Headquarters Location</div>
              <div>Founded Year</div>
              <div>Company Website</div>
              <div>Growth Indicators</div>
              <div>Technology Usage</div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Helps determine if the company fits your ideal customer profile.</p>
          </details>

          <details className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <summary className="cursor-pointer text-white font-semibold">Data Validation &amp; Quality Signals</summary>
            <p className="text-gray-400 text-sm mt-2">Seamless includes metadata that supports contact accuracy.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-300 mt-3">
              <div>Email Validation Status</div>
              <div>Phone Confidence Score</div>
              <div>Contact Match Score</div>
              <div>Data Source Indicators</div>
              <div>Last Updated Timestamp</div>
            </div>
            <p className="text-xs text-gray-500 mt-3">Confidence metrics help prioritize high-quality records.</p>
          </details>

          <details className="bg-gray-900 rounded-lg p-4 border border-gray-800">
            <summary className="cursor-pointer text-white font-semibold">Extended Profile Attributes</summary>
            <p className="text-gray-400 text-sm mt-2">
              Additional structured fields used for filtering, exporting, and CRM mapping.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-300 mt-3">
              <div>Role Classification</div>
              <div>Department Tags</div>
              <div>Seniority Tier</div>
              <div>Company Size Brackets</div>
              <div>Geographic Tags</div>
              <div>Industry Codes</div>
              <div>Social Profile Links</div>
              <div>Alternative Contact Methods</div>
            </div>
            <p className="text-xs text-gray-500 mt-3">These additional fields support advanced filtering and segmentation.</p>
          </details>

          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-4 border border-gray-800">
            <p className="text-gray-200 text-sm">
              A Seamless profile is more than contact information — it’s a structured dataset designed for prospecting, segmentation, and CRM integration.
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-900/40 border border-blue-700/50 text-blue-200 text-sm font-semibold">
              This is just the start of our lead intelligence system.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 text-sm">
              <div className="bg-black/40 rounded-lg p-3 border border-gray-800">
                <div className="text-gray-400">Basic Contact List</div>
                <div className="text-white font-semibold mt-1">Name + Email</div>
              </div>
              <div className="bg-black/40 rounded-lg p-3 border border-gray-800">
                <div className="text-gray-400">Seamless Profile</div>
                <div className="text-white font-semibold mt-1">Identity + Company + Verification + Structured Attributes</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Schedule Modal
export function ScheduleModal({ mode }: { mode?: 'company' | 'default' } = {}) {
  const { userEmail, timezone, adminToken } = useAppStore();
  const { scheduleModalOpen, closeScheduleModal, filters } = useResearchStore();
  const [name, setName] = useState('');
  const [days, setDays] = useState<string[]>([]);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [creditLimit, setCreditLimit] = useState(100);
  const [researchMode, setResearchMode] = useState<'regular_pepai' | 'super_scrape'>('regular_pepai');
  const [campaignOptions, setCampaignOptions] = useState<InstantlyCampaignOption[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [ghlTargetOptions, setGhlTargetOptions] = useState<Array<{ locationId: string; label: string }>>([]);
  const [selectedGhlLocationId, setSelectedGhlLocationId] = useState('');
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!scheduleModalOpen) return;
    let active = true;
    const loadScheduleDestinations = async () => {
      if (!userEmail || !adminToken) return;
      setLoadingDestinations(true);
      try {
        const [accountResult, campaignsResult] = await Promise.allSettled([
          getAccountSettings({ token: adminToken, email: userEmail }),
          listInstantlyCampaignOptions({ userEmail }),
        ]);
        if (!active) return;

        if (accountResult.status === 'fulfilled' && accountResult.value?.success) {
          const rawOptions = Array.isArray(accountResult.value.ghlSendAsOptions)
            ? accountResult.value.ghlSendAsOptions
            : [];
          const options = rawOptions
            .filter((opt) => opt?.active !== false)
            .map((opt) => {
              const locationId = String(opt.locationId ?? '').trim();
              const name = String(opt.name ?? '').trim();
              return {
                locationId,
                label: name && name !== locationId ? `${name} (${locationId})` : locationId,
              };
            })
            .filter((opt) => Boolean(opt.locationId));
          setGhlTargetOptions(options);
          const savedLocationId = String(accountResult.value.account?.ghlSendAsLocationId ?? '').trim();
          setSelectedGhlLocationId((current) =>
            current && options.some((opt) => opt.locationId === current)
              ? current
              : savedLocationId && options.some((opt) => opt.locationId === savedLocationId)
                ? savedLocationId
                : ''
          );
        } else {
          setGhlTargetOptions([]);
          setSelectedGhlLocationId('');
        }

        if (campaignsResult.status === 'fulfilled' && campaignsResult.value?.success) {
          const campaigns = Array.isArray(campaignsResult.value.campaigns)
            ? campaignsResult.value.campaigns
            : [];
          setCampaignOptions(campaigns);
          const defaultCampaignId = String(campaignsResult.value.defaultCampaign?.id ?? '').trim();
          setSelectedCampaignId((current) =>
            current && campaigns.some((campaign) => campaign.id === current)
              ? current
              : defaultCampaignId && campaigns.some((campaign) => campaign.id === defaultCampaignId)
                ? defaultCampaignId
                : campaigns[0]?.id ?? ''
          );
        } else {
          setCampaignOptions([]);
          setSelectedCampaignId('');
        }
      } finally {
        if (active) setLoadingDestinations(false);
      }
    };

    loadScheduleDestinations().catch(() => {
      if (!active) return;
      setCampaignOptions([]);
      setSelectedCampaignId('');
      setGhlTargetOptions([]);
      setSelectedGhlLocationId('');
      setLoadingDestinations(false);
    });

    return () => {
      active = false;
    };
  }, [adminToken, scheduleModalOpen, userEmail]);

  const toggleDay = (day: string) => {
    setDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const summarizeFilters = () => {
    const parts = [];
    if (filters.titles.length) parts.push(`Titles: ${filters.titles.join(', ')}`);
    if (filters.seniorities.length) parts.push(`Seniority: ${filters.seniorities.join(', ')}`);
    if (filters.departments.length) parts.push(`Departments: ${filters.departments.join(', ')}`);
    if (filters.industries.length) parts.push(`Industries: ${filters.industries.join(', ')}`);
    if (filters.locations.length) parts.push(`Locations: ${filters.locations.join(', ')}`);
    if (filters.employeeSizes.length) parts.push(`Size: ${filters.employeeSizes.join(', ')}`);
    return parts.length ? parts.join(' | ') : 'No filters selected';
  };

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Please enter a name');
      return;
    }
    if (days.length === 0) {
      alert('Select at least one day');
      return;
    }

    const creditCostPerCompany = researchMode === 'super_scrape' ? 2 : 1;
    const selectedCampaign =
      selectedCampaignId
        ? campaignOptions.find((campaign) => campaign.id === selectedCampaignId) ?? null
        : null;
    const targetGhlLocationId = selectedGhlLocationId || '';
    setSaving(true);
    try {
      const payload = {
        name,
        userEmail,
        filters,
        scheduleDays: days,
        scheduleHour: hour,
        scheduleMinute: minute,
        creditLimit,
        researchMode,
        instantlyCampaignId: selectedCampaignId || undefined,
        instantlyCampaignName: selectedCampaign?.name || undefined,
        targetGhlLocationId: targetGhlLocationId || undefined,
        ghlTargetLocationId: targetGhlLocationId || undefined,
        timezone,
        perPage: Math.max(1, Math.floor(creditLimit / creditCostPerCompany)),
      };
      const result = await createScheduledSearch(payload);

      if (result.success) {
        alert(`Scheduled search "${name}" created!`);
        closeScheduleModal();
      } else {
        alert('Failed to create schedule');
      }
    } catch (e) {
      alert('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const shortDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hourOptions = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21];
  const minuteOptions = [0, 15, 30, 45];
  const formatHourLabel = (h: number) => {
    if (h === 0) return '12';
    if (h === 12) return '12';
    return h > 12 ? String(h - 12) : String(h);
  };

  if (!scheduleModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <div className="bg-card-dark border border-border-dark rounded-xl max-w-lg w-full p-6 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">📅 Schedule Recurring Search</h3>
          <button onClick={closeScheduleModal} className="text-gray-400 hover:text-white text-2xl">
            <X className="w-6 h-6" />
          </button>
        </div>
        <p className="text-gray-400 mb-4">Automatically research leads with these filters on a recurring schedule.</p>
        {mode === 'company' && (
          <div className="mb-4 rounded-lg border border-emerald-600/30 bg-emerald-900/20 px-3 py-2 text-sm text-emerald-200">
            Bonus offer: Get Data + Crunchbase in Company Search schedules and <span className="font-semibold">save 1 credit per company</span>.
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Search Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weekly Sales VPs"
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Run on these days</label>
            <div className="flex flex-wrap gap-2">
              {dayOptions.map((day, idx) => (
                <label key={day} className="flex items-center space-x-1 text-sm">
                  <input
                    type="checkbox"
                    checked={days.includes(day)}
                    onChange={() => toggleDay(day)}
                    className="rounded"
                  />
                  <span className="text-gray-300">{shortDays[idx]}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Time to run ({timezone})
            </label>
            <p className="text-xs text-gray-500 mb-2">Runs at quarter-hour intervals (00, 15, 30, 45).</p>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={hour}
                onChange={(e) => setHour(Number(e.target.value))}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
              >
                {hourOptions.map((h) => (
                  <option key={h} value={h}>
                    {formatHourLabel(h)} {h >= 12 ? 'PM' : 'AM'}
                  </option>
                ))}
              </select>
              <select
                value={minute}
                onChange={(e) => setMinute(Number(e.target.value))}
                className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
              >
                {minuteOptions.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Research mode</label>
            <select
              value={researchMode}
              onChange={(e) => setResearchMode(e.target.value as 'regular_pepai' | 'super_scrape')}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
            >
              <option value="regular_pepai">Regular Research + PEPAI (1 credit per lead)</option>
              <option value="super_scrape">SuperScrape (2 credits per lead)</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Regular Research + PEPAI runs Seamless + PEPAI flow. SuperScrape runs the async super-scrape enrichment flow.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">CRM Integration Target</label>
            <select
              value={selectedGhlLocationId}
              onChange={(e) => setSelectedGhlLocationId(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
            >
              <option value="">Use account default CRM routing</option>
              {ghlTargetOptions.map((option) => (
                <option key={`schedule-ghl-${option.locationId}`} value={option.locationId}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              {loadingDestinations ? 'Loading integrations...' : 'Choose which CRM location this schedule should target.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email Campaign</label>
            <select
              value={selectedCampaignId}
              onChange={(e) => setSelectedCampaignId(e.target.value)}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
            >
              <option value="">No campaign override</option>
              {campaignOptions.map((campaign) => (
                <option key={`schedule-campaign-${campaign.id}`} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              {loadingDestinations
                ? 'Loading campaigns...'
                : 'Optional: enroll scheduled contacts into this Instantly campaign.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Credits per run</label>
            <select
              value={creditLimit}
              onChange={(e) => setCreditLimit(Number(e.target.value))}
              className="w-full px-3 py-2 bg-black border border-gray-700 rounded text-white text-sm"
            >
              {[50, 100, 500].map((c) => (
                <option key={c} value={c}>{c} credits</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Max companies per run: {Math.max(1, Math.floor(creditLimit / (researchMode === 'super_scrape' ? 2 : 1)))}
            </p>
          </div>

          <div className="bg-gray-900 p-3 rounded text-sm">
            <p className="text-gray-400"><strong className="text-white">Current Filters:</strong></p>
            <p className="text-gray-500 mt-1">{summarizeFilters()}</p>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-700 rounded text-white font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Save Search Modal
export function SaveSearchModal() {
  const { userEmail } = useAppStore();
  const { saveSearchModalOpen, closeSaveSearchModal, filters, perPage, setSavedSearches } = useResearchStore();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  if (!saveSearchModalOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Enter a name');
      return;
    }
    if (!userEmail) {
      alert('Missing user email');
      return;
    }
    setSaving(true);
    try {
      const result = await createSavedSearch({ userEmail, name: name.trim(), filters, perPage });
      if (!result.success) throw new Error('Save failed');
      const refreshed = await listSavedSearches(userEmail);
      if (refreshed.success) setSavedSearches(refreshed.savedSearches);
      setName('');
      closeSaveSearchModal();
      alert('Search saved!');
    } catch (e) {
      alert('Failed to save search');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center">
      <div className="bg-card-dark border border-border-dark rounded-xl max-w-md w-full p-6 max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-white">💾 Save This Search</h3>
          <button onClick={closeSaveSearchModal} className="text-gray-400 hover:text-white text-2xl">
            <X className="w-6 h-6" />
          </button>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Search name (e.g., VP Sales Texas)"
          className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white mb-4"
        />
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Search'}
        </button>
      </div>
    </div>
  );
}

// Saved Searches Drawer
export function SavedSearchesDrawer() {
  const {
    savedSearchesDrawerOpen,
    closeSavedSearchesDrawer,
    savedSearches,
    recentSearches,
    loadSavedSearch,
    setFilters,
    setActiveSavedSearchId,
    setCurrentPage,
    setPerPage,
    setPendingSearch,
  } = useResearchStore();

  if (!savedSearchesDrawerOpen) return null;

  const summarizeFilters = (f: typeof savedSearches[0]['filters']) => {
    const p = [];
    if (f.titles?.length) p.push(f.titles.slice(0, 2).join(', '));
    if (f.seniorities?.length) p.push(f.seniorities[0]);
    if (f.industries?.length) p.push(f.industries[0]);
    if (f.locations?.length) p.push(f.locations[0]);
    return p.join(' • ') || 'All filters';
  };

  const handleLoad = (index: number, type: 'saved' | 'recent') => {
    const search = loadSavedSearch(index, type);
    if (!search) return;
    const filters = (search as { filters?: typeof savedSearches[0]['filters'] }).filters;
    if (!filters) return;

    setFilters(filters);
    if (type === 'saved') {
      const saved = search as { id: string; lastPage?: number; perPage?: number };
      setActiveSavedSearchId(saved.id);
      const page = typeof saved.lastPage === 'number' ? saved.lastPage : 0;
      const pp = typeof saved.perPage === 'number' ? saved.perPage : 25;
      setPerPage(pp);
      setCurrentPage(page);
      setPendingSearch({ filters, page, perPage: pp });
    } else {
      setActiveSavedSearchId(null);
      setCurrentPage(0);
      setPendingSearch({ filters, page: 0, perPage: 25 });
    }
    closeSavedSearchesDrawer();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={closeSavedSearchesDrawer}
      />
      <div className="fixed top-0 left-0 w-80 h-full bg-gray-900 border-r border-gray-700 z-50">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white">Saved Searches</h3>
          <button onClick={closeSavedSearchesDrawer} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
          {savedSearches.length > 0 && (
            <>
              <h4 className="text-xs text-gray-500 uppercase mb-2">Saved</h4>
              {savedSearches.map((s, i) => (
                <div
                  key={i}
                  onClick={() => handleLoad(i, 'saved')}
                  className="bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-700 mb-2"
                >
                  <div className="font-semibold text-white text-sm">{s.name}</div>
                  <div className="text-xs text-gray-400">
                    {new Date(s.savedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </>
          )}

          {recentSearches.length > 0 && (
            <>
              <h4 className="text-xs text-gray-500 uppercase mb-2 mt-4">Recent</h4>
              {recentSearches.map((s, i) => (
                <div
                  key={i}
                  onClick={() => handleLoad(i, 'recent')}
                  className="bg-gray-800 p-3 rounded cursor-pointer hover:bg-gray-700 mb-2"
                >
                  <div className="text-sm text-gray-300">{summarizeFilters(s.filters)}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(s.searchedAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </>
          )}

          {savedSearches.length === 0 && recentSearches.length === 0 && (
            <p className="text-gray-500 text-sm">No saved searches yet.</p>
          )}
        </div>
      </div>
    </>
  );
}
